"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva(
  "inline-flex items-center text-muted-foreground",
  {
    variants: {
      variant: {
        line: "border-b border-gray-200 px-1 w-full",
        enclosed: "border-b border-gray-200",
        solid: "bg-gray-50 rounded-md p-1",
        subtle: "bg-transparent rounded-md",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  }
)

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap px-4 py-3 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        line: "border-b-2 border-transparent -mb-px data-[state=active]:border-palomma-primary data-[state=active]:text-palomma-primary hover:text-palomma-primary",
        enclosed: "rounded-t-md border border-transparent -mb-px data-[state=active]:text-palomma-primary data-[state=active]:border-gray-200 data-[state=active]:border-b-white",
        solid: "rounded-md data-[state=active]:bg-palomma-primary/70 data-[state=active]:text-white data-[state=active]:shadow-[0_2px_4px_rgba(64,18,171,0.2)] hover:bg-palomma-primary/10",
        subtle: "rounded-md data-[state=active]:bg-palomma-primary/60 data-[state=active]:text-white data-[state=active]:shadow-[0_1px_3px_rgba(64,18,171,0.15)] hover:bg-palomma-primary/5",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  }
)

type TabsVariant = VariantProps<typeof tabsListVariants>["variant"]

const TabsVariantContext = React.createContext<TabsVariant>("line")

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant = "line", ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  </TabsVariantContext.Provider>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
